import {useCallback, useMemo} from 'react';
import {User} from 'firebase/auth';
import {addDoc, collection, deleteDoc, doc, query, serverTimestamp, updateDoc, where} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {Planner} from '../types/Planner';
import {firestore} from "../App"; // Import your planner type

// Define the shape of the hook's props
interface UsePlannerManagerProps {
    user: User;
}

// These match the old hook's options, but we'll map `title` to `name`
interface PlannerInsertOptions {
    title: string;
}

interface PlannerUpdateOptions {
    title: string;
}

/**
 * Provides functions for managing changes to the planners in Cloud Firestore,
 * such as adding, updating, and deleting planners.
 */
export function usePlannerManager({user}: UsePlannerManagerProps) {
    // 1. READ Planners (Replaces useQuery)
    const plannersQuery = useMemo(() => {
        return query(
            collection(firestore, 'syncedPlanners'),
            where(`roles.${user.uid}.role`, 'in', ['owner', 'writer', 'reader'])
        );
    }, [user.uid]);

    const [plannersSnapshot, loadingPlanners, errorPlanners] = useCollection(plannersQuery);

    const planners = useMemo(() => {
        return plannersSnapshot?.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Planner)) || [];
    }, [plannersSnapshot]);

    /**
     * Creates a new planner document in Firestore.
     */
    const createPlanner = useCallback(async (options: PlannerInsertOptions) => {
        const plannerData = {
            name: options.title, // Map title to name
            createdOn: serverTimestamp(),
            roles: {
                [user.uid]: {
                    role: 'owner'
                }
            }
        };
        await addDoc(collection(firestore, 'syncedPlanners'), plannerData);
    }, [user.uid]);

    /**
     * Updates an existing planner document in Firestore.
     */
    const updatePlanner = useCallback(async (plannerId: string, options: PlannerUpdateOptions) => {
        const docRef = doc(firestore, 'syncedPlanners', plannerId);
        await updateDoc(docRef, {
            name: options.title // Map title to name
        });
    }, []);

    /**
     * Deletes a planner document from Firestore.
     * * !!! WARNING: This only deletes the planner document itself.
     * It does NOT delete any subcollections (homework, exams, etc.).
     * Those documents will be orphaned.
     * See the "Warning" section below.
     */
    const deletePlanner = useCallback(async (plannerId: string) => {
        const docRef = doc(firestore, 'syncedPlanners', plannerId);
        await deleteDoc(docRef);
    }, []);

    return {
        planners,
        loadingPlanners,
        errorPlanners,
        createPlanner,
        updatePlanner,
        deletePlanner
    };
}